import React from 'react'

/**
 * Stepper — Figma element (Performance Appraisal Platform, node 887:16956).
 * See docs/frontend-design-specs.md §15.
 *
 * Numbered circles on a connector line with labels underneath. The Figma
 * file defines only current + pending states (no completed/checkmark
 * state); steps before `current` render with the current styling since
 * they have been reached.
 *
 * steps: [{ label, caption?, number? }] — `number` overrides the ordinal
 * circle text (sessions can number phases non-contiguously, e.g. 1, 2, 6).
 * current: index of the active step.
 */
export default function Stepper({ steps = [], current = 0, compact = false }) {
  if (!steps.length) return null
  if (compact) {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {steps.map((step, index) => {
          const reached = index <= current
          return (
            <React.Fragment key={step.key || index}>
              {index > 0 && <div style={{ width: 14, borderTop: '2px solid #cccccc' }} />}
              <div
                title={step.caption ? `${step.label}: ${step.caption}` : step.label}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: `2px solid ${reached ? '#323a3c' : '#cccccc'}`,
                  background: 'transparent',
                  color: reached ? '#323a3c' : '#cccccc',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                {step.number ?? index + 1}
              </div>
            </React.Fragment>
          )
        })}
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 8, width: '100%' }}>
      {steps.map((step, index) => {
        const reached = index <= current
        const circleStyle = {
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: `2px solid ${reached ? '#323a3c' : '#cccccc'}`,
          background: 'transparent',
          color: reached ? '#323a3c' : '#cccccc',
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }
        return (
          <React.Fragment key={step.key || index}>
            {index > 0 && (
              <div style={{ flex: '1 1 0', height: 40, display: 'flex', alignItems: 'center', minWidth: 16 }}>
                <div style={{ width: '100%', borderTop: '2px solid #cccccc' }} />
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 90 }}>
              <div style={circleStyle}>{step.number ?? index + 1}</div>
              <div style={{ fontSize: 16, lineHeight: 1.5, color: '#475254', textAlign: 'center' }}>{step.label}</div>
              {step.caption && (
                <div style={{ fontSize: 12, lineHeight: 1.4, color: '#a3aeac', textAlign: 'center', marginTop: -4 }}>{step.caption}</div>
              )}
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}
